/**
 * Схема синхронизируемых сущностей EdPad Teacher.
 *
 * ВАЖНО: backend хранит эти данные в универсальной таблице sync_record как
 * JSONB-payload. Backend не валидирует поля — клиент полностью владеет схемой.
 * Поэтому изменения в типах ниже не требуют миграции БД.
 *
 * Каждая сущность должна иметь:
 *  - id: string (uuid v4)
 *  - createdAt: number (ms timestamp)
 *  - updatedAt: number (ms timestamp) — используется для LWW-conflict resolution
 *  - deletedAt: number | null (soft delete)
 */

// ─── Base ─────────────────────────────────────────────────────────────────
export interface SyncEntity {
  id: string
  createdAt: number
  updatedAt: number
  deletedAt?: number | null
}

// ─── Classes ──────────────────────────────────────────────────────────────
export interface SchoolClass extends SyncEntity {
  name: string                    // "5А", "10Б"
  gradeLevel: number              // 1..11
  yearStart: number               // 2025 (учебный год 2025/2026)
  description?: string | null
  isClassTeacher?: boolean        // классный руководитель?
  archivedAt?: number | null      // архивирован, не отображать по дефолту
}

// ─── Students ─────────────────────────────────────────────────────────────
export interface ParentContact {
  name: string
  relation: 'mother' | 'father' | 'guardian' | 'other'
  phone?: string
  email?: string
}

export interface Student extends SyncEntity {
  classId: string                 // FK → SchoolClass.id
  lastName: string
  firstName: string
  middleName?: string | null
  gender?: 'M' | 'F' | null
  birthDate?: string | null       // ISO date "2010-05-15"
  parentContacts?: ParentContact[]
  medicalGroup?: 'main' | 'preparatory' | 'special' | 'exempt' | null
  specialNeeds?: string | null    // АООП, ОВЗ, ИУП
  notes?: string | null
  tags?: string[]                 // ['актив', 'олимпиадник']
  nativeLanguage?: string | null  // для билингв
  arrivedAt?: string | null       // когда пришёл в школу (ISO date)
  leftAt?: string | null          // когда выбыл (ISO date)
}

// ─── Subjects ─────────────────────────────────────────────────────────────
export interface Subject extends SyncEntity {
  name: string                    // "Математика", "Русский язык"
  shortName?: string              // "Матем", "Рус"
  color?: string                  // hex для визуализации в расписании
  teacherId?: string              // owner — учитель, который ведёт
}

// ─── Grades (оценки) ──────────────────────────────────────────────────────
export type GradeScale =
  | 'fivePoint'    // 1-5 (российская стандартная)
  | 'tenPoint'     // 1-10
  | 'hundredPoint' // 0-100
  | 'passFail'     // зачёт / незачёт
  | 'letter'       // A B C D F

export type GradeType =
  | 'current'      // текущая (за урок)
  | 'test'         // контрольная / самостоятельная
  | 'exam'         // экзамен
  | 'project'      // проект
  | 'homework'     // ДЗ
  | 'term'         // четвертная
  | 'year'         // годовая

export interface Grade extends SyncEntity {
  studentId: string
  subjectId: string
  classId: string                 // для индексирования по классу
  date: string                    // ISO date "2026-05-27"
  value: string                   // "5", "92", "зач", "B+" — зависит от шкалы
  scale: GradeScale
  type: GradeType
  weight?: number                 // вес 0.0-2.0 для weighted average
  comment?: string | null
  topic?: string | null           // тема урока
}

// ─── Attendance (посещаемость) ────────────────────────────────────────────
export type AttendanceMark =
  | 'present'      // присутствовал
  | 'absent'       // отсутствовал
  | 'late'         // опоздал
  | 'sick'         // болезнь (с справкой)
  | 'excused'      // уважительная (с заявлением)
  | 'unexcused'    // неуважительная

export interface Attendance extends SyncEntity {
  studentId: string
  classId: string
  subjectId?: string | null       // null если общая запись за день
  date: string                    // ISO date
  mark: AttendanceMark
  lateMinutes?: number            // если mark === 'late'
  reason?: string | null
}

// ─── Document (сохранённый LLM-результат или ручной документ) ────────────
export type DocumentKind =
  | 'lessonPlan'
  | 'quiz'
  | 'rubric'
  | 'characteristic'
  | 'parentLetter'
  | 'worksheet'
  | 'selfEducation'
  | 'classPortrait'
  | 'quarterReport'
  | 'topicAnalysis'
  | 'textLeveler'
  | 'vision'
  | 'custom'

export interface Document extends SyncEntity {
  kind: DocumentKind
  title: string
  content: string                 // markdown / plain text
  classId?: string
  studentId?: string
  subjectId?: string
  date?: string                   // для каких уроков/событий
  tags?: string[]
  llmModel?: string               // gigachat / yandexgpt / mock
  isFavorite?: boolean
}

// ─── Notification (in-app уведомление) ────────────────────────────────────
export type NotificationType =
  | 'template_published'
  | 'template_pending_review'   // учитель предложил шаблон → завучу
  | 'template_approved'         // завуч одобрил → автору
  | 'template_rejected'         // завуч отклонил → автору (с причиной в body)
  | 'school_invite_accepted'
  | 'school_join'
  | 'low_llm_limit'
  | 'student_alert'
  | 'system'

export interface Notification extends SyncEntity {
  type: NotificationType
  title: string
  body: string
  link?: string                   // куда вести при клике, например '/app/templates'
  readAt?: number | null
}

// ─── Lesson (урок в расписании) ───────────────────────────────────────────
export interface Lesson extends SyncEntity {
  classId: string
  subjectId: string
  dayOfWeek: 1 | 2 | 3 | 4 | 5 | 6 | 7  // ISO: 1=Пн ... 7=Вс
  slot: number                     // номер урока в дне 1..8
  startTime?: string               // "09:00"
  endTime?: string                 // "09:45"
  cabinet?: string                 // "212"
  topic?: string                   // конкретная тема урока (если задаётся для расписания)
}

// ─── Generic sync record (для api/sync.ts) ───────────────────────────────
// Backend хранит payload плоско: что push — то и pull возвращает.
// При pull добавляется serverTimestamp в payload.

export interface SyncPullResponse<T extends SyncEntity = SyncEntity> {
  records: Array<T & { serverTimestamp?: number }>
  serverTimestamp: number          // для следующего pull (передать в ?since=)
}

// ─── Homework (домашние задания) ──────────────────────────────────────────
export interface Homework extends SyncEntity {
  classId: string
  subjectId: string
  date: string             // ISO date — дата урока, на который задано
  dueDate: string          // ISO date — срок сдачи
  description: string
  attachmentUrl?: string | null
}

// ─── BehaviorRecord (поведенческие баллы) ────────────────────────────────
export type BehaviorCategory =
  | 'help'           // помощь одноклассникам
  | 'leadership'     // лидерство
  | 'activity'       // активность
  | 'respect'        // уважение
  | 'discipline'     // нарушение дисциплины
  | 'late'           // опоздание
  | 'homework_miss'  // невыполнение ДЗ
  | 'custom'

export interface BehaviorRecord extends SyncEntity {
  studentId: string
  classId: string
  date: string             // ISO date
  category: BehaviorCategory
  points: number           // +1..+5 (позитив) или -1..-5 (негатив)
  note?: string | null
}

// ─── Entity type names (для path /sync/v2/{entity}) ─────────────────────
export const ENTITY = {
  schoolClass: 'school_class',
  student:     'student_v2',
  subject:     'subject',
  grade:       'grade',
  attendance:  'attendance',
  lesson:      'lesson',
  document:    'document',
  notification:'notification',
  homework:    'homework',
  behavior:    'behavior',
} as const
export type EntityType = (typeof ENTITY)[keyof typeof ENTITY]
